import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';

const MEAL_COLORS = { breakfast: 'amber', lunch: 'emerald', dinner: 'indigo' };

function getCurrentMealType(mealTimes) {
  const now = new Date();
  const hhmm = now?.getHours() * 60 + now?.getMinutes();
  for (const mt of mealTimes) {
    if (!mt?.is_active) continue;
    const [sh, sm] = mt?.start_time?.split(':')?.map(Number);
    const [eh, em] = mt?.end_time?.split(':')?.map(Number);
    if (hhmm >= sh * 60 + sm && hhmm <= eh * 60 + em) return mt?.meal_type;
  }
  return null;
}

export default function MealValidation() {
  const [cardUid, setCardUid] = useState('');
  const [mealTimes, setMealTimes] = useState([]);
  const [currentMeal, setCurrentMeal] = useState(null);
  const [scanResult, setScanResult] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [recentScans, setRecentScans] = useState([]);
  const inputRef = useRef(null);

  useEffect(() => {
    fetchMealTimes();
    fetchRecentScans();
    inputRef?.current?.focus();
  }, []);

  useEffect(() => {
    if (mealTimes?.length > 0) {
      const meal = getCurrentMealType(mealTimes);
      setCurrentMeal(meal);
      const interval = setInterval(() => setCurrentMeal(getCurrentMealType(mealTimes)), 60000);
      return () => clearInterval(interval);
    }
  }, [mealTimes]);

  const fetchMealTimes = async () => {
    const { data } = await supabase?.from('restaurant_meal_times')?.select('*')?.order('start_time');
    setMealTimes(data || []);
  };

  const fetchRecentScans = async () => {
    const { data } = await supabase?.from('restaurant_scan_logs')?.select('*, restaurant_cardholders(full_name, category)')?.order('scan_time', { ascending: false })?.limit(20);
    setRecentScans(data || []);
  };

  const handleScan = async (e) => {
    e?.preventDefault();
    if (!cardUid?.trim()) return;
    setScanning(true);
    setScanResult(null);
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      const today = new Date()?.toISOString()?.split('T')?.[0];

      // Find card
      const { data: card } = await supabase?.from('restaurant_nfc_cards')?.select('*, restaurant_cardholders(*)')?.eq('card_uid', cardUid?.trim()?.toUpperCase())?.eq('is_active', true)?.maybeSingle();

      if (!card || !card?.restaurant_cardholders) {
        setScanResult({ type: 'error', title: 'Card Not Found', message: 'This NFC card is not registered or inactive.', icon: 'ShieldX', color: 'red' });
        await logScan(null, null, null, 'denied_no_eligibility', user?.id);
        setCardUid('');
        setScanning(false);
        return;
      }

      const ch = card?.restaurant_cardholders;

      // Check accommodation
      if (ch?.accommodation_end && new Date(ch.accommodation_end) < new Date()) {
        setScanResult({ type: 'error', title: 'Accommodation Expired', message: `${ch?.full_name}'s accommodation ended on ${ch?.accommodation_end}.`, icon: 'CalendarX', color: 'red' });
        await logScan(ch?.id, card?.id, null, 'denied_expired', user?.id);
        setCardUid('');
        setScanning(false);
        return;
      }

      // Check meal time
      if (!currentMeal) {
        setScanResult({ type: 'warning', title: 'Outside Meal Hours', message: 'No meal service is currently active. Please check meal schedule.', icon: 'Clock', color: 'amber' });
        await logScan(ch?.id, card?.id, null, 'denied_wrong_time', user?.id);
        setCardUid('');
        setScanning(false);
        return;
      }

      // Check meal eligibility
      const mealKey = `meal_${currentMeal}`;
      if (!ch?.[mealKey]) {
        setScanResult({ type: 'error', title: 'Meal Not Included', message: `${ch?.full_name} does not have ${currentMeal} in their meal plan.`, icon: 'Ban', color: 'red' });
        await logScan(ch?.id, card?.id, null, 'denied_no_eligibility', user?.id);
        setCardUid('');
        setScanning(false);
        return;
      }

      // Check duplicate scan today
      const { data: existingScan } = await supabase?.from('restaurant_scan_logs')?.select('id')?.eq('cardholder_id', ch?.id)?.eq('meal_type', currentMeal)?.eq('scan_result', 'approved')?.gte('scan_time', `${today}T00:00:00`)?.lte('scan_time', `${today}T23:59:59`)?.maybeSingle();

      if (existingScan) {
        setScanResult({ type: 'error', title: 'Already Scanned', message: `${ch?.full_name} already received ${currentMeal} today. Duplicate scan blocked.`, icon: 'AlertCircle', color: 'orange' });
        await logScan(ch?.id, card?.id, null, 'denied_duplicate', user?.id);
        setCardUid('');
        setScanning(false);
        return;
      }

      // Approved
      await logScan(ch?.id, card?.id, null, 'approved', user?.id);
      setScanResult({
        type: 'success',
        title: 'Access Granted',
        message: `Welcome, ${ch?.full_name}! Enjoy your ${currentMeal}.`,
        icon: 'CheckCircle2',
        color: 'emerald',
        cardholder: ch,
        meal: currentMeal,
      });
      fetchRecentScans();
    } catch (err) {
      console.error(err);
      setScanResult({ type: 'error', title: 'System Error', message: 'An unexpected error occurred. Please try again.', icon: 'AlertTriangle', color: 'red' });
    } finally {
      setCardUid('');
      setScanning(false);
      setTimeout(() => inputRef?.current?.focus(), 100);
    }
  };

  const logScan = async (cardholderId, cardId, ticketId, result, userId) => {
    await supabase?.from('restaurant_scan_logs')?.insert({
      cardholder_id: cardholderId,
      nfc_card_id: cardId,
      meal_ticket_id: ticketId,
      meal_type: currentMeal,
      scan_result: result,
      scanned_by: userId,
    });
  };

  const resultColors = { emerald: 'bg-emerald-50 border-emerald-300 text-emerald-800', red: 'bg-red-50 border-red-300 text-red-800', amber: 'bg-amber-50 border-amber-300 text-amber-800', orange: 'bg-orange-50 border-orange-300 text-orange-800' };
  const scanResultColors = { approved: 'text-emerald-600', denied_duplicate: 'text-orange-500', denied_expired: 'text-red-500', denied_no_eligibility: 'text-red-500', denied_wrong_time: 'text-amber-500' };
  const scanResultLabels = { approved: 'Approved', denied_duplicate: 'Duplicate', denied_expired: 'Expired', denied_no_eligibility: 'Not Eligible', denied_wrong_time: 'Wrong Time' };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Scanner Panel */}
      <div className="space-y-5">
        {/* Current Meal Status */}
        <div className={`rounded-xl border-2 p-5 ${currentMeal ? `border-${MEAL_COLORS?.[currentMeal]}-300 bg-${MEAL_COLORS?.[currentMeal]}-50` : 'border-slate-200 bg-slate-50'}`}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${currentMeal ? `bg-${MEAL_COLORS?.[currentMeal]}-200` : 'bg-slate-200'}`}>
              <Icon name={currentMeal === 'breakfast' ? 'Coffee' : currentMeal === 'lunch' ? 'UtensilsCrossed' : currentMeal === 'dinner' ? 'Moon' : 'Clock'} size={20} className={currentMeal ? `text-${MEAL_COLORS?.[currentMeal]}-700` : 'text-slate-500'} />
            </div>
            <div>
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Current Meal Period</div>
              <div className={`text-lg font-black capitalize ${currentMeal ? `text-${MEAL_COLORS?.[currentMeal]}-700` : 'text-slate-500'}`}>
                {currentMeal || 'No Active Meal Period'}
              </div>
            </div>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2">
            {mealTimes?.map(mt => (
              <div key={mt?.meal_type} className={`text-center p-2 rounded-lg text-xs ${mt?.meal_type === currentMeal ? `bg-${MEAL_COLORS?.[mt?.meal_type]}-200 font-bold` : 'bg-white/60'}`}>
                <div className="font-semibold capitalize">{mt?.meal_type}</div>
                <div className="text-muted-foreground">{mt?.start_time?.slice(0,5)} – {mt?.end_time?.slice(0,5)}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Scan Input */}
        <div className="bg-card border border-border rounded-xl p-5 space-y-4">
          <h3 className="font-semibold text-foreground flex items-center gap-2">
            <Icon name="Scan" size={16} className="text-primary" />
            NFC Card Scanner
          </h3>
          <form onSubmit={handleScan} className="space-y-3">
            <input
              ref={inputRef}
              type="text"
              placeholder="Scan or type NFC Card UID..."
              value={cardUid}
              onChange={e => setCardUid(e?.target?.value)}
              className="w-full border-2 border-primary/30 rounded-xl px-4 py-3 text-base font-mono bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary"
              autoComplete="off"
            />
            <button type="submit" disabled={scanning || !cardUid?.trim()}
              className="w-full py-3 rounded-xl bg-primary text-primary-foreground font-bold text-base disabled:opacity-50 flex items-center justify-center gap-2 hover:opacity-90 transition-all">
              {scanning ? <Icon name="Loader2" size={18} className="animate-spin" /> : <Icon name="Wifi" size={18} />}
              {scanning ? 'Validating...' : 'Validate Meal'}
            </button>
          </form>
        </div>

        {/* Scan Result */}
        {scanResult && (
          <div className={`border-2 rounded-xl p-5 ${resultColors?.[scanResult?.color]}`}>
            <div className="flex items-start gap-3">
              <Icon name={scanResult?.icon} size={28} />
              <div>
                <div className="font-bold text-lg">{scanResult?.title}</div>
                <div className="text-sm mt-1">{scanResult?.message}</div>
                {scanResult?.cardholder && (
                  <div className="mt-2 text-xs space-y-0.5">
                    <div><span className="font-semibold">Category:</span> {scanResult?.cardholder?.category}</div>
                    <div><span className="font-semibold">Building:</span> {scanResult?.cardholder?.building || '—'}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Recent Scans */}
      <div className="bg-card border border-border rounded-xl p-5 space-y-3">
        <h3 className="font-semibold text-foreground flex items-center gap-2">
          <Icon name="History" size={16} className="text-primary" />
          Recent Scans
        </h3>
        <div className="space-y-2 max-h-[520px] overflow-y-auto pr-1">
          {recentScans?.map(s => (
            <div key={s?.id} className="flex items-center gap-3 p-3 rounded-lg bg-muted/40 border border-border">
              <div className={`w-2 h-2 rounded-full flex-shrink-0 ${s?.scan_result === 'approved' ? 'bg-emerald-500' : 'bg-red-400'}`} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-foreground truncate">{s?.restaurant_cardholders?.full_name || 'Unknown'}</div>
                <div className="text-xs text-muted-foreground capitalize">{s?.meal_type} · {s?.restaurant_cardholders?.category}</div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className={`text-xs font-semibold ${scanResultColors?.[s?.scan_result]}`}>{scanResultLabels?.[s?.scan_result]}</div>
                <div className="text-xs text-muted-foreground">{new Date(s.scan_time)?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
              </div>
            </div>
          ))}
          {recentScans?.length === 0 && (
            <div className="py-8 text-center text-sm text-muted-foreground">No scans yet today</div>
          )}
        </div>
      </div>
    </div>
  );
}
